import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const stateLabels = {
  trialing: 'Trialing',
  active: 'Active',
  grace_period: 'Grace Period',
  restricted: 'Restricted',
  canceled: 'Canceled',
  complimentary: 'Complimentary',
  grandfathered: 'Grandfathered',
  internal_test: 'Internal Test',
  incomplete_signup: 'Incomplete Signup',
  past_due: 'Past Due'
};

const formatState = (value) => stateLabels[value] || value?.replaceAll('_', ' ') || 'Unknown';

const formatMoney = (cents) => {
  if (typeof cents !== 'number') return 'Not set';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(cents / 100);
};

const formatDate = (value) => {
  if (!value) return 'Not set';
  return new Date(value).toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric'
  });
};

const daysUntil = (dateValue) => {
  if (!dateValue) return null;
  const diff = new Date(dateValue).getTime() - Date.now();
  return Math.max(Math.ceil(diff / (1000 * 60 * 60 * 24)), 0);
};

const getFunctionErrorMessage = async (invokeError, fallback) => {
  const response = invokeError?.context;

  if (response?.clone) {
    try {
      const payload = await response.clone().json();
      return payload?.error || payload?.message || invokeError.message || fallback;
    } catch {
      try {
        const text = await response.clone().text();
        return text || invokeError.message || fallback;
      } catch {
        return invokeError.message || fallback;
      }
    }
  }

  return invokeError?.message || fallback;
};

const AdvisorBilling = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [summary, setSummary] = useState(null);
  const [account, setAccount] = useState(null);
  const [usageEvents, setUsageEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);

  const billingReturnState = searchParams.get('billing');

  const fetchBilling = async ({ quiet = false } = {}) => {
    if (!profile) return;

    if (!profile.advisor_firm_id) {
      setLoading(false);
      setError('Advisor billing is only available for advisor firm users.');
      return;
    }

    try {
      if (quiet) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const [summaryRes, accountRes, eventsRes] = await Promise.all([
        supabase.rpc('current_advisor_billing_summary'),
        supabase
          .from('advisor_billing_accounts')
          .select(`
              id,
              account_category,
              subscription_state,
              access_state,
              billing_interval,
              standard_amount_cents,
              actual_amount_cents,
              discount_amount_cents,
              discount_description,
              included_monthly_submissions,
              bonus_submissions_current_period,
              trial_started_at,
              trial_ends_at,
              grace_period_ends_at,
              complimentary_until,
              stripe_environment,
              stripe_customer_id,
              stripe_subscription_id,
              stripe_subscription_status,
              current_period_start,
              current_period_end,
              cancel_at_period_end,
              canceled_at,
              plan:subscription_plans(display_name, plan_key)
            `)
          .eq('advisor_firm_id', profile.advisor_firm_id)
          .maybeSingle(),
        supabase
          .from('advisor_usage_events')
          .select(`
              id,
              event_type,
              quantity,
              is_billable_usage,
              created_at,
              metadata,
              matter:matters(
                id,
                plan_type,
                status,
                client:clients(first_name, last_name, email)
              )
            `)
          .eq('advisor_firm_id', profile.advisor_firm_id)
          .order('created_at', { ascending: false })
          .limit(20)
      ]);

      if (summaryRes.error) throw summaryRes.error;
      if (accountRes.error) throw accountRes.error;
      if (eventsRes.error) throw eventsRes.error;

      setSummary(Array.isArray(summaryRes.data) ? summaryRes.data[0] : summaryRes.data);
      setAccount(accountRes.data || null);
      setUsageEvents(eventsRes.data || []);
    } catch (err) {
      console.error('Advisor billing load error:', err);
      setError(err.message || 'Unable to load billing account.');
    } finally {
      if (quiet) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchBilling();
  }, [profile]);

  const handleStartCheckout = async (billingInterval) => {
    if (checkoutLoading) return;

    try {
      setCheckoutLoading(billingInterval);
      setActionError(null);

      const { data, error: checkoutError } = await supabase.functions.invoke('create-billing-checkout', {
        body: {
          billing_interval: billingInterval,
          origin: window.location.origin
        }
      });

      if (checkoutError) {
        throw new Error(await getFunctionErrorMessage(checkoutError, 'Unable to start billing checkout.'));
      }

      if (!data?.url) throw new Error('Stripe checkout did not return a redirect URL.');
      window.location.assign(data.url);
    } catch (err) {
      console.error('Billing checkout error:', err);
      setActionError(err.message || 'Unable to start billing checkout.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleOpenPortal = async () => {
    if (portalLoading) return;

    try {
      setPortalLoading(true);
      setActionError(null);

      const { data, error: portalError } = await supabase.functions.invoke('create-billing-portal', {
        body: {
          origin: window.location.origin
        }
      });

      if (portalError) {
        throw new Error(await getFunctionErrorMessage(portalError, 'Unable to open billing portal.'));
      }

      if (!data?.url) throw new Error('Stripe portal did not return a redirect URL.');
      window.location.assign(data.url);
    } catch (err) {
      console.error('Billing portal error:', err);
      setActionError(err.message || 'Unable to open billing portal.');
    } finally {
      setPortalLoading(false);
    }
  };

  const canStartCheckout = summary && ['trialing', 'restricted', 'incomplete_signup'].includes(summary.access_state);
  const returnNotice = {
    checkout_success: {
      icon: FiIcons.FiCheckCircle,
      className: 'bg-green-50 border-green-400 text-green-800',
      title: 'Stripe checkout completed.',
      message: 'Your account will update as soon as Stripe sends the confirmation webhook. Refresh if it still looks pending.'
    },
    checkout_cancelled: {
      icon: FiIcons.FiAlertCircle,
      className: 'bg-amber-50 border-amber-400 text-amber-800',
      title: 'Stripe checkout was cancelled.',
      message: 'No billing change was made. You can restart activation whenever you are ready.'
    },
    portal_return: {
      icon: FiIcons.FiCheckCircle,
      className: 'bg-blue-50 border-blue-400 text-blue-800',
      title: 'Returned from Stripe.',
      message: 'Any payment, invoice, or subscription updates will appear here after Stripe confirms them.'
    }
  }[billingReturnState];
  const includedSubmissions = summary?.included_submissions ?? account?.included_monthly_submissions ?? 0;
  const bonusSubmissions = summary?.bonus_submissions ?? account?.bonus_submissions_current_period ?? 0;
  const usedSubmissions = summary?.used_submissions ?? 0;
  const remainingSubmissions = summary?.remaining_submissions ?? Math.max(includedSubmissions + bonusSubmissions - usedSubmissions, 0);
  const totalSubmissions = includedSubmissions + bonusSubmissions;
  const usagePercent = totalSubmissions > 0 ? Math.min((usedSubmissions / totalSubmissions) * 100, 100) : 0;
  const trialDaysRemaining = daysUntil(account?.trial_ends_at || summary?.trial_ends_at);

  if (loading) {
    return (
      <div className="py-16 text-center text-slate-400">
        <SafeIcon icon={FiIcons.FiLoader} className="animate-spin h-8 w-8 mx-auto mb-3" />
        <p className="text-sm font-medium">Loading billing account...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md text-sm text-red-700">
        {error}
      </div>
    );
  }

  if (!summary || !account) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <SafeIcon icon={FiIcons.FiCreditCard} className="h-10 w-10 text-slate-300 mx-auto mb-3" />
        <h1 className="text-xl font-bold text-slate-900">No billing account found</h1>
        <p className="mt-2 text-sm text-slate-500">A Platform Admin can review this advisor firm account.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Billing & Account</h1>
          <p className="mt-1 text-sm text-slate-500">Subscription status, monthly attorney submissions, and account details.</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center px-4 py-2 rounded-md border border-slate-300 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <SafeIcon icon={FiIcons.FiArrowLeft} className="mr-2 h-4 w-4" />
          Dashboard
        </button>
      </div>

      {actionError && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md text-sm text-red-700">
          {actionError}
        </div>
      )}

      {returnNotice && (
        <div className={`border-l-4 p-4 rounded-md ${returnNotice.className}`}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-start">
              <SafeIcon icon={returnNotice.icon} className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-bold">{returnNotice.title}</p>
                <p className="mt-1 text-sm opacity-90">{returnNotice.message}</p>
              </div>
            </div>
            <div className="flex gap-2 md:flex-shrink-0">
              <button
                type="button"
                onClick={() => fetchBilling({ quiet: true })}
                disabled={refreshing}
                className="inline-flex items-center px-3 py-2 rounded-md bg-white/80 text-xs font-bold uppercase tracking-widest hover:bg-white disabled:opacity-50"
              >
                {refreshing && <SafeIcon icon={FiIcons.FiLoader} className="animate-spin mr-2 h-4 w-4" />}
                Refresh
              </button>
              <button
                type="button"
                onClick={() => setSearchParams({})}
                className="inline-flex items-center px-3 py-2 rounded-md bg-white/60 text-xs font-bold uppercase tracking-widest hover:bg-white"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-bold text-slate-900">{account.plan?.display_name || summary.plan_name || 'Advisor Plan'}</h2>
              <span className="px-2.5 py-0.5 rounded-full border bg-blue-50 text-blue-700 border-blue-100 text-[10px] font-bold uppercase tracking-wider">
                {formatState(account.access_state)}
              </span>
              <span className="px-2.5 py-0.5 rounded-full border bg-slate-50 text-slate-600 border-slate-200 text-[10px] font-bold uppercase tracking-wider">
                Stripe {account.stripe_environment}
              </span>
            </div>
            <p className="mt-2 text-sm text-slate-500">
              {account.access_state === 'trialing'
                ? `Trial account${trialDaysRemaining !== null ? ` with ${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'} remaining` : ''}.`
                : `Subscription is ${formatState(account.subscription_state).toLowerCase()}.`}
            </p>
          </div>

          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Current Price</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{formatMoney(account.actual_amount_cents)}</p>
              <p className="text-xs text-slate-500 capitalize">{account.billing_interval || 'monthly'}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Trial Ends</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{formatDate(account.trial_ends_at)}</p>
              <p className="text-xs text-slate-500">Attorney submission requires active billing</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Period Ends</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{formatDate(account.current_period_end || summary.period_end)}</p>
              <p className="text-xs text-slate-500">{account.cancel_at_period_end ? 'Cancels at period end' : 'Auto-renewing if subscribed'}</p>
            </div>
          </div>

          <div className="px-6 pb-6">
            <div className="rounded-lg border border-slate-200 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-bold text-slate-900 uppercase tracking-tighter">Monthly Attorney Submissions</h3>
                  <p className="mt-1 text-sm text-slate-500">
                    {usedSubmissions} used, {remainingSubmissions} remaining this period.
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-slate-900">{remainingSubmissions}</p>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Remaining</p>
                </div>
              </div>
              <div className="mt-4 h-3 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-blue-600" style={{ width: `${usagePercent}%` }} />
              </div>
              <div className="mt-3 grid grid-cols-3 gap-3 text-xs text-slate-500">
                <div><span className="font-bold text-slate-700">{includedSubmissions}</span> included</div>
                <div><span className="font-bold text-slate-700">{bonusSubmissions}</span> bonus</div>
                <div><span className="font-bold text-slate-700">{usedSubmissions}</span> used</div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
          <div>
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-tighter">Account Actions</h2>
            <p className="mt-1 text-sm text-slate-500">Manage activation and billing access.</p>
          </div>

          {canStartCheckout ? (
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => handleStartCheckout('monthly')}
                disabled={Boolean(checkoutLoading)}
                className="w-full inline-flex items-center justify-center px-4 py-2 rounded-md bg-slate-900 text-white text-xs font-bold uppercase tracking-widest hover:bg-slate-800 disabled:opacity-50"
              >
                {checkoutLoading === 'monthly' ? (
                  <SafeIcon icon={FiIcons.FiLoader} className="animate-spin mr-2 h-4 w-4" />
                ) : (
                  <SafeIcon icon={FiIcons.FiCreditCard} className="mr-2 h-4 w-4" />
                )}
                Activate Monthly
              </button>
              <button
                type="button"
                onClick={() => handleStartCheckout('annual')}
                disabled={Boolean(checkoutLoading)}
                className="w-full inline-flex items-center justify-center px-4 py-2 rounded-md bg-white text-slate-700 border border-slate-300 text-xs font-bold uppercase tracking-widest hover:bg-slate-50 disabled:opacity-50"
              >
                Activate Annual
              </button>
            </div>
          ) : (
            <div className="rounded-lg bg-green-50 border border-green-100 p-3 text-sm text-green-800">
              This account is currently allowed to submit plans according to its access state.
            </div>
          )}

          <div className="pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={handleOpenPortal}
              disabled={portalLoading || !account.stripe_customer_id}
              className="w-full inline-flex items-center justify-center px-4 py-2 rounded-md bg-white text-slate-700 border border-slate-300 text-xs font-bold uppercase tracking-widest hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {portalLoading ? (
                <SafeIcon icon={FiIcons.FiLoader} className="animate-spin mr-2 h-4 w-4" />
              ) : (
                <SafeIcon icon={FiIcons.FiExternalLink} className="mr-2 h-4 w-4" />
              )}
              Manage Billing In Stripe
            </button>
            <p className="mt-2 text-xs text-slate-500">
              Use Stripe Customer Portal to update payment methods, view invoices, or manage subscription settings.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
          <h2 className="text-sm font-bold text-slate-800 uppercase tracking-tighter">Recent Usage Events</h2>
        </div>

        {usageEvents.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/70">
                <tr>
                  <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest">Date</th>
                  <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest">Event</th>
                  <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest">Plan</th>
                  <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest">Client</th>
                  <th className="px-6 py-3 text-right text-[10px] font-bold text-slate-400 uppercase tracking-widest">Qty</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {usageEvents.map((event) => (
                  <tr key={event.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 text-sm text-slate-600">{formatDate(event.created_at)}</td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-semibold text-slate-900">{formatState(event.event_type)}</div>
                      <div className="text-xs text-slate-500">{event.is_billable_usage ? 'Billable usage' : 'Non-billable'}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{event.matter?.plan_type || 'Not linked'}</td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-slate-900">
                        {[event.matter?.client?.first_name, event.matter?.client?.last_name].filter(Boolean).join(' ') || 'Not linked'}
                      </div>
                      <div className="text-xs text-slate-500">{event.matter?.client?.email}</div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-bold text-slate-900">{event.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center text-slate-500 text-sm">
            No attorney submission usage has been recorded yet.
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvisorBilling;
